/**
 * WorkflowResults - Main results view that displays multiple WorkflowToolResults
 *
 * This component:
 * - Shows all workflow tool outputs in a list format
 * - Filters to only show nodes referenced in workflow outputs
 * - Provides TopBar actions for sharing and downloading all results
 * - Can optionally support Panel detail views for individual results
 */
// import {
//   DropdownMenu,
//   DropdownMenuContent,
//   DropdownMenuItem,
//   DropdownMenuTrigger,
// } from '@/components/ui/dropdown-menu'
import { TopBarAction, TopBarButton } from '@/components/ui/TopBar'
import { TopBarActionId } from '@/components/ui/TopBarActionIds'
import {
  downloadAllToolResults,
  exportWorkflowToMarkdown,
  WorkflowToolResult,
} from '@/components/WorkflowToolResults/WorkflowToolResult'
import { useScrollbarVisibility } from '@/hooks/useScrollbarVisibility'
import { convertMarkdownToDocx, convertMarkdownToPdf } from '@/lib/docx-utils'
import { useCopilotContext } from '@/lib/features/copilotv2/CopilotContext'
import { filterNodeExecutionsForOutputs } from '@/lib/utils/workflowFilterUtils'
import { Download, File, FileText, Share2, Trash2 } from 'lucide-react'
import Image from 'next/image'
import React, { useCallback, useEffect, useMemo } from 'react'
import { toast } from 'react-toastify'
import { NodeExecution } from 'sprites-types/api/v2/workflows'
import { WorkflowConfig } from 'sprites-types/workflow/core'

interface WorkflowResultsProps {
  workflow?: WorkflowConfig
  enablePanelView?: boolean
  onOpenPanel?: (content: any, breadcrumbs: any[]) => void
}

interface UseWorkflowResultsActionsProps {
  workflow?: WorkflowConfig
  executionId?: string
  hasResults: boolean
  processedExecutions: Array<{
    executionId: string
    workflowRef?: string
    workflowConfig?: WorkflowConfig
    nodes: NodeExecution[]
    createdAt: string
  }>
}

export function useWorkflowResultsActions({
  workflow,
  executionId,
  hasResults,
  processedExecutions,
}: UseWorkflowResultsActionsProps): TopBarAction[] {
  const { workflowState, clearAllResults } = useCopilotContext()

  // Debug logging
  console.log('[WorkflowResults] useWorkflowResultsActions called with:', {
    hasWorkflow: !!workflow,
    executionId,
    hasResults,
    nodeExecutionsCount: workflowState.nodeExecutions
      ? Object.keys(workflowState.nodeExecutions).length
      : 0,
  })
  const handleShare = useCallback(() => {
    // Collect all unique execution IDs from node executions
    const nodeExecutions = workflowState.nodeExecutions
      ? Object.values(workflowState.nodeExecutions)
      : []

    const executionIds = new Set<string>()
    nodeExecutions.forEach((nodeExec) => {
      // Extract workflow execution ID from node execution ID (format: node_execution_{workflowUuid}_{nodeUuid})
      // We want to get workflow_execution_{workflowUuid}
      const match = nodeExec.executionId.match(/^node_execution_([0-9a-f-]+)_/)
      if (match) {
        executionIds.add(`workflow_execution_${match[1]}`)
      }
    })

    console.log('[WorkflowResults] handleShare - extracted execution IDs:', {
      nodeExecutionsCount: nodeExecutions.length,
      sampleExecutionIds: nodeExecutions.slice(0, 3).map((n) => n.executionId),
      extractedIds: Array.from(executionIds),
      fallbackExecutionId: executionId,
    })

    // Fallback to single executionId if available and no executions found
    if (executionIds.size === 0 && executionId) {
      executionIds.add(executionId)
    }

    if (executionIds.size === 0) return

    // Create comma-separated list for URL
    const executionIdList = Array.from(executionIds).join(',')
    const textToCopy = `${process.env.NEXT_PUBLIC_APP_URI}/results/${executionIdList}`

    console.log('[WorkflowResults] Copying to clipboard:', textToCopy)

    navigator.clipboard.writeText(textToCopy).catch((err) => {
      console.error('[WorkflowResults] Failed to copy text:', err)
    })
    toast.success('Link Copied to Clipboard')
  }, [executionId, workflowState.nodeExecutions])

  const handleDownloadAll = useCallback(async () => {
    try {
      // Flatten all filtered node executions from all workflow executions
      const allFilteredNodes = processedExecutions.flatMap((exec) => exec.nodes)
      if (allFilteredNodes.length === 0) return

      // Use first available workflow config or fallback to prop
      const workflowConfig = processedExecutions[0]?.workflowConfig || workflow
      if (!workflowConfig) return

      await downloadAllToolResults(allFilteredNodes, workflowConfig)
      toast.success('Started downloads for all results')
    } catch (err) {
      console.error('[WorkflowResults] Failed to download all:', err)
      toast.error('Failed to download all results')
    }
  }, [workflow, processedExecutions])

  const handleDownloadPdf = useCallback(async () => {
    try {
      // Flatten all filtered node executions from all workflow executions
      const allFilteredNodes = processedExecutions.flatMap((exec) => exec.nodes)

      // Use first available workflow config or fallback to prop
      const workflowConfig = processedExecutions[0]?.workflowConfig || workflow
      if (!workflowConfig) return

      const markdown = exportWorkflowToMarkdown(allFilteredNodes, workflowConfig)
      const blob = await convertMarkdownToPdf(markdown)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `workflow-results-${executionId || 'latest'}.pdf`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('PDF downloaded')
    } catch (err) {
      console.error('[WorkflowResults] Failed to download PDF:', err)
      toast.error('Failed to download PDF')
    }
  }, [workflow, executionId, processedExecutions])

  const handleDownloadDocx = useCallback(async () => {
    try {
      // Flatten all filtered node executions from all workflow executions
      const allFilteredNodes = processedExecutions.flatMap((exec) => exec.nodes)

      // Use first available workflow config or fallback to prop
      const workflowConfig = processedExecutions[0]?.workflowConfig || workflow
      if (!workflowConfig) return

      const markdown = exportWorkflowToMarkdown(allFilteredNodes, workflowConfig)
      const blob = await convertMarkdownToDocx(markdown)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `workflow-results-${executionId || 'latest'}.docx`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('DOCX downloaded')
    } catch (err) {
      console.error('[WorkflowResults] Failed to download DOCX:', err)
      toast.error('Failed to download DOCX')
    }
  }, [workflow, executionId, processedExecutions])

  const handleClearAll = useCallback(() => {
    try {
      if (!hasResults) return

      const confirmed = window.confirm(
        'Are you sure you want to clear all results? This action cannot be undone.'
      )

      if (!confirmed) return

      clearAllResults()
      toast.success('Results cleared')
    } catch (err) {
      console.error('[WorkflowResults] Failed to clear results:', err)
      toast.error('Failed to clear results')
    }
  }, [hasResults, clearAllResults])

  const actions = useMemo(
    () => [
      {
        id: TopBarActionId.WORKFLOW_RESULTS_CLEAR,
        element: (
          <TopBarButton
            onClick={handleClearAll}
            title={!hasResults ? 'No results to clear yet' : 'Clear all results'}
            disabled={!hasResults}
          >
            <Trash2 size={16} />
          </TopBarButton>
        ),
        visible: hasResults,
        order: 0,
      },
      {
        id: TopBarActionId.WORKFLOW_RESULTS_SHARE,
        element: (
          <TopBarButton
            onClick={handleShare}
            title={!hasResults ? 'No results to share yet' : 'Share results'}
            disabled={!hasResults}
          >
            <Share2 size={16} />
          </TopBarButton>
        ),
        visible: hasResults,
        order: 1,
      },
      {
        id: TopBarActionId.WORKFLOW_RESULTS_DOWNLOAD,
        element: (
          <div className="relative">
            <TopBarButton
              data-download-button
              onClick={() => {
                console.log('[WorkflowResults] Download button clicked!')
                // Toggle dropdown visibility
                const dropdown = document.getElementById('download-dropdown')
                if (dropdown) {
                  dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none'
                }
              }}
              title={!hasResults ? 'No results to download yet' : 'Download results'}
              disabled={false} // Temporarily always enabled for debugging
            >
              <Download size={16} />
            </TopBarButton>
            <div
              id="download-dropdown"
              className="absolute left-0 top-full z-[9999] mt-1 w-48 overflow-visible rounded-md border border-gray-200 bg-white shadow-lg"
              style={{ display: 'none' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="flex cursor-pointer items-center px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100"
                onClick={() => {
                  console.log('[WorkflowResults] Download All clicked!')
                  handleDownloadAll()
                  document.getElementById('download-dropdown')!.style.display = 'none'
                }}
              >
                <Download className="mr-2 h-4 w-4" />
                <span>Download All Files</span>
              </div>
              <div
                className="flex cursor-pointer items-center px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100"
                onClick={() => {
                  console.log('[WorkflowResults] Download PDF clicked!')
                  handleDownloadPdf()
                  document.getElementById('download-dropdown')!.style.display = 'none'
                }}
              >
                <FileText className="mr-2 h-4 w-4" />
                <span>Download as PDF</span>
              </div>
              <div
                className="flex cursor-pointer items-center px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100"
                onClick={() => {
                  console.log('[WorkflowResults] Download DOCX clicked!')
                  handleDownloadDocx()
                  document.getElementById('download-dropdown')!.style.display = 'none'
                }}
              >
                <File className="mr-2 h-4 w-4" />
                <span>Download as DOCX</span>
              </div>
            </div>
          </div>
        ),
        visible: true, // Temporarily always visible for debugging
        order: 2,
      },
    ],
    [
      hasResults,
      handleClearAll,
      handleShare,
      handleDownloadAll,
      handleDownloadPdf,
      handleDownloadDocx,
    ]
  )

  // Debug logging for actions
  console.log(
    '[WorkflowResults] Generated actions:',
    actions.map((a) => ({ id: a.id, visible: a.visible }))
  )

  // Add click-outside handler to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const dropdown = document.getElementById('download-dropdown')
      const button = event.target as Element

      if (dropdown && !dropdown.contains(button) && !button.closest('[data-download-button]')) {
        dropdown.style.display = 'none'
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return actions
}

const WorkflowResults: React.FC<WorkflowResultsProps> = ({
  workflow,
  enablePanelView = false,
  onOpenPanel,
}) => {
  const { context } = useCopilotContext()

  // Debug logging
  console.log('[WorkflowResults] Component rendered with:', {
    hasWorkflow: !!workflow,
    enablePanelView,
    workflowExecutionsCount: Object.keys(context.workflowExecutions || {}).length,
  })

  // Process workflow executions and filter node executions based on outputs
  const processedExecutions = useMemo(() => {
    if (!context.workflowExecutions) {
      return []
    }

    const executions = Object.values(context.workflowExecutions).map((execution) => {
      // Get all node executions for this workflow execution
      const allNodeExecutions = Object.values(execution.nodeExecutions)

      // Filter based on outputNodeExecutionIds if available
      let filteredNodes: NodeExecution[]
      if (execution.outputNodeExecutionIds && execution.outputNodeExecutionIds.length > 0) {
        const outputIdsSet = new Set(execution.outputNodeExecutionIds)
        filteredNodes = allNodeExecutions
          .filter((exec) => outputIdsSet.has(exec.executionId))
          .filter((exec) => exec.status !== 'failed' && exec.status !== 'errored')
      } else {
        // Fall back to workflow config outputs filtering
        const workflowConfig = execution.workflowConfig || workflow
        if (workflowConfig) {
          filteredNodes = filterNodeExecutionsForOutputs(allNodeExecutions, workflowConfig).filter(
            (exec) => exec.status !== 'failed' && exec.status !== 'errored'
          )
        } else {
          // Final fallback: show all if no config available
          filteredNodes = allNodeExecutions.filter(
            (exec) => exec.status !== 'failed' && exec.status !== 'errored'
          )
        }
      }

      return {
        executionId: execution.executionId,
        workflowRef: execution.workflowRef,
        workflowConfig: execution.workflowConfig,
        nodes: filteredNodes,
        createdAt: execution.createdAt,
      }
    })

    // Sort by creation time (newest first)
    executions.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    console.log('[WorkflowResults] Processed executions:', {
      executionCount: executions.length,
      executions: executions.map((e) => ({
        execId: e.executionId,
        nodeCount: e.nodes.length,
        hasWorkflowConfig: !!e.workflowConfig,
      })),
    })

    return executions
  }, [context.workflowExecutions])

  // Ignore executions without renderable results to avoid blank dividers
  const executionsWithResults = useMemo(
    () => processedExecutions.filter((execution) => execution.nodes.length > 0),
    [processedExecutions]
  )

  const hasResults = executionsWithResults.length > 0

  const scrollContainerRef = React.useRef<HTMLDivElement>(null)

  // Handle scrollbar visibility on scroll
  useScrollbarVisibility(scrollContainerRef)

  return (
    <div
      ref={scrollContainerRef}
      className="scrollbar-auto relative z-0 flex h-full w-full flex-col overflow-y-auto overflow-x-hidden bg-white/20"
    >
      <div className="relative w-full">
        <div className="flex w-full flex-col gap-y-4 p-4">
          {hasResults ? (
            executionsWithResults.map((execution, executionIndex) => {
              // Use execution-specific workflow config or fall back to prop
              const workflowConfig = execution.workflowConfig || workflow

              if (!workflowConfig) {
                return null
              }

              return (
                <React.Fragment key={execution.executionId}>
                  {executionIndex > 0 && (
                    <div className="relative my-6 flex items-center justify-center px-8">
                      <div className="absolute inset-0 flex items-center px-8">
                        <div className="w-full border-t border-gray-200"></div>
                      </div>
                    </div>
                  )}
                  {execution.nodes.map((nodeExecution, index) => (
                    <WorkflowToolResult
                      key={`node_${nodeExecution.nodeId}_${index}`}
                      nodeExecution={nodeExecution}
                      workflowConfig={workflowConfig}
                      onOpenPanel={enablePanelView ? onOpenPanel : undefined}
                    />
                  ))}
                </React.Fragment>
              )
            })
          ) : (
            // Empty state when no results to show
            <div className="flex h-full min-h-[400px] flex-col items-center justify-center">
              <div className="text-center">
                <Image
                  width={200}
                  height={200}
                  alt="No results"
                  src="/images/dashboard/no-result.png"
                  className="mx-auto mb-6"
                />
                <h3 className="mb-2 text-lg font-medium text-gray-900">No results to display</h3>
                <p className="mx-auto max-w-sm text-gray-600">
                  The workflow execution didn&apos;t produce any output results to show here. Check
                  the workflow logs for more details about the execution.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default WorkflowResults
